import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithColumns,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type CollapsibleContainerBehaviorModel,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
  type PropertiesEditorItemValueChanged,
} from '@blaze-shared/elements-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { convertCollapsibleContainerBehaviorStateToExpression } from '../../utils/elements/expressions.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { CollapsibleContainerDefinitionMapper } from './ai/collapsible-container.definition-mapper';
import { CollapsibleContainerElementHandler } from './ai/collapsible-container.element-handler';
import { CollapsibleContainerComponent } from './collapsible-container.component';
import {
  type CollapsibleContainerPropsModel,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  INITIAL_SLOT_ID,
} from './collapsible-container.models';
import { collapsibleContainerV2Migration } from './migrations/002-collapsible-container.migration';
import { collapsibleContainerV3Migration } from './migrations/003-collapsible-container.migration';

export type CollapsibleContainerDef = WithColumns &
  WithProps<CollapsibleContainerPropsModel> &
  WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    collapsibleContainer: CollapsibleContainerDef;
  }
}

registerElementDefinition({
  type: 'collapsibleContainer',
  componentType: CollapsibleContainerComponent,
  isListed: true,
  name: 'Collapsible Container',
  groups: ['containers'],
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  // By default the element should have a min-height and grow automatically if needed
  height: { mode: 'auto', value: DEFAULT_HEIGHT, fixed: false },
  slots: {
    ids: [INITIAL_SLOT_ID],
    [INITIAL_SLOT_ID]: {
      children: [],
    },
  },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  styleIncludePaths: ['style'],
  features: {
    preventAutoWidth: true,
  },

  version: 3,
  migrations: {
    2: collapsibleContainerV2Migration,
    3: collapsibleContainerV3Migration,
  },
  ai: {
    name: 'collapsibleContainer',
    handler: () => CollapsibleContainerElementHandler,
    definitionMapper: () => CollapsibleContainerDefinitionMapper,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
      {
        name: 'opened',
        bindingDefs: [{ property: 'opened', label: 'Opened?', dataType: 'Boolean' }],
      },
    ],
    outputs: [],
  };
}

function getDefaults(): PropsOf<'collapsibleContainer'> {
  return {
    content: {
      header: {
        title: 'Title',
        showChevron: true,
        chevron: 'fas-chevron-down',
        reverseChevron: true,
        align: 'left',
        enabled: true,
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      behavior: {
        states: ['open'],
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal', padding: '$t.paddingContainerLike' },
      },
      titleBlock: {
        fontSize: '14px',
        fontStyle: '$t.headerFontStyle' as any,
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '16px',
      },
      container: {
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        outlineWidth: '$t.containerOutlineWidth',
        radius: '$t.radius',
      },
      header: {
        title: { color1: '$t.title', selectorType: 'solid' },
        icon: { color1: '$t.title', selectorType: 'solid' },
        fill: { color1: '$t.canvas', selectorType: 'none' },
      },
      content: {
        divider: { color1: '$t.outline', selectorType: 'solid' },
        fill: { color1: '$t.canvas', selectorType: 'none' },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'collapsibleContainer'> {
  return {
    style: {
      titleBlock: {
        fontSize: '$t.headerFontSize',
        iconSize: '$t.headerIconSize',
      },
    },
  };
}

function collapsibleContainerBehaviorField(
  path: string,
): PropertiesConfigItemDefinition<'collapsibleContainerBehavior'> {
  return {
    path,
    label: 'Behavior',
    inputType: 'collapsibleContainerBehavior',
  };
}

function updateCollapsibleContainerBehaviorExpression(): PropertiesEditorItemValueChanged {
  return (model: any, value: CollapsibleContainerBehaviorModel) => {
    const expression = convertCollapsibleContainerBehaviorStateToExpression(value);
    return {
      'data.?key:opened.dataSource.sourceType': 'expression',
      'data.?key:opened.dataSource.expression': expression,
      'data.?key:opened.enabled': true,
    };
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('collapsibleContainer', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content.header', 'Header', [
        epc.fields.iconField('content.header.icon', 'Image', { placeholder: 'Optional' }),
        epc.fields.textAreaField('content.header.title', 'Title'),
        epc.fields.toggleField('content.header.showChevron', 'Show Icon'),
        ...epc.utils.conditionalFields(
          [
            epc.fields.iconField('content.header.chevron', 'Icon', { required: true }),
            epc.fields.toggleField('content.header.reverseChevron', 'Reverse icon on toggle', {
              renderType: 'check',
              offsetToggle: true,
            }),
          ],
          'content.header.showChevron',
          (showChevron: boolean) => !showChevron,
        ),
      ]),
      epc.subsections.subsection('content.behavior', 'Container Behavior', [
        epc.utils.observedField(
          collapsibleContainerBehaviorField('content.behavior'),
          updateCollapsibleContainerBehaviorExpression(),
        ),
      ]),
      epc.subsections.visibilitySubsection(),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding & Margin & Alignment', [
          epc.fields.spacingAndAlignContainerField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.container', 'General', {
          hasFill: false,
          hasOutlineSides: true,
        }),
        epc.subsections.subsection('style.header', 'Header', [
          epc.fields.colorField('style.header.icon', 'Open & close icon', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.header.fill', 'Fill', {}),
        ]),
        epc.subsections.subsection('style.content', 'Content', [
          epc.fields.colorField('style.content.divider', 'Divider', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.content.fill', 'Fill', {}),
        ]),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.header.enabled',
          color: 'style.header.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.header.align',
          icon: 'content.header.icon',
          iconPosition: 'content.header.iconPosition',
          iconColor: 'content.header.iconColor',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
